import { useDroppable } from '@dnd-kit/core'

import { BoardCard } from '@/features/boards/BoardCard'
import type { StateCategory } from '@/types/domain'
import type { BoardColumn as Column } from '@/types/work'
import { NewTask } from '@/features/work-items/NewTask'

const CATEGORY_DOT: Record<StateCategory, string> = {
  backlog: 'var(--color-state-backlog)',
  started: 'var(--color-state-started)',
  blocked: 'var(--color-state-blocked)',
  done: 'var(--color-state-done)',
  cancelled: 'var(--color-state-cancelled)',
}

export function BoardColumn({
  column,
  droppableId,
  projectId,
}: {
  column: Column
  droppableId: string
  projectId?: string | undefined
}) {
  const { setNodeRef, isOver } = useDroppable({ id: droppableId })

  // A WIP limit is a warning, never a block. A limit that stops someone working
  // gets removed within a week. The judgement is the server's, so the board,
  // the table and the timeline all agree on when a column is full.
  const overLimit = column.overWipLimit

  return (
    <section
      ref={setNodeRef}
      className={`flex w-72 shrink-0 flex-col rounded-md border ${
        isOver ? 'border-brand-300 bg-brand-50' : 'border-line bg-surface-2'
      }`}
    >
      <header className="border-line flex items-center gap-2 border-b px-3 py-2.5">
        <span
          aria-hidden="true"
          className="size-2 shrink-0 rounded-full"
          style={{ backgroundColor: CATEGORY_DOT[column.category] }}
        />
        <h3 className="text-ink-900 min-w-0 flex-1 truncate text-sm font-medium">{column.name}</h3>
        <span
          title={column.wipLimit === null ? undefined : `WIP ${column.wipLimit}`}
          className={`font-mono text-xs tabular-nums ${
            overLimit ? 'font-semibold text-[color:var(--color-state-blocked)]' : 'text-ink-500'
          }`}
        >
          {column.count}
          {column.wipLimit === null ? '' : ` / ${column.wipLimit}`}
        </span>
      </header>

      <div className="flex min-h-24 flex-col gap-2 p-2">
        {column.items.map((item) => (
          <BoardCard key={item.id} item={item} />
        ))}

        {projectId === undefined ? null : (
          <NewTask projectId={projectId} stateId={column.id} />
        )}
      </div>
    </section>
  )
}
